import {
  clone,
  exec,
  exec_later,
  groupBy,
  indexBy,
  merge_by,
  sortBy,
  to_chunks,
} from "@/fp";
import {
  getAllInsights,
  getInsightsDesc,
  get_insights_sender_receiver_counts,
  updateInsight,
} from "./insights.api";
import { AppsInsightSeverityLevel, EventLabelDir, Insight } from "../../models";
import { state } from "../state";
import { AppAnalytics } from "@/analytics";
import { computed } from "@vue/composition-api";
import { ToastType, UseToast } from "@/components/root/toast/toast.state";

const { action, getter, m, mutation } = state;

//#region insights
export const insights = getter((s) => s.insights);
export const indexed_insights = computed(() =>
  indexBy<Insight>("id")([...insights.value])
);
export const non_information_risk_insights = getter((s) =>
  s.insights.filter((l) => l.shareType !== "informationRisk")
);
export const insightsGrouped = getter((s) =>
  groupBy<Insight>("severity", s.insights)
);

const setInsights = m.set("insights");
const merge_by_id = merge_by<Insight, "id">("id");
export const compliance_insights = computed(() =>
  insights.value.filter((d) => d.type === "Compliance")
);

const update_all_insights = mutation<
  Array<{
    id: string;
    exposedToCount: number;
    exposedByCount: number;
    eventsCount: number;
  }>
>("update insight counters", (s, arr) => {
  return {
    ...s,
    insights: merge_by_id(s.insights)(arr) as Insight[],
  };
});

export const loadInsights = action<void>("get insights", async ({ mutate }) => {
  const res = await getAllInsights();
  const descRes = await getInsightsDesc();
  if (res.success && descRes.success) {
    const insights = res.value.map((insight) => {
      return {
        ...insight,
        description: descRes.value[insight.id] || "",
      };
    });

    mutate(
      setInsights,
      sortBy<Insight, "severity">(insights, "severity", true)
    );
  }
});

export const updateInsightsCounters = action<void>(
  "update insights counter",
  async ({ state, mutate }) => {
    mutate(
      setInsights,
      state.insights.map((insight) => {
        return {
          ...insight,
          isLoaded: false,
        };
      })
    );
    const insightsIds = state.insights
      .filter((ins) => ins.severity !== "None")
      .map((insight) => ({
        id: insight.id,
        eventLabel: insight.eventLabel,
      }));

    const chunks_of_ids = to_chunks<{ id: string; eventLabel: EventLabelDir }>(
      50
    )(insightsIds);

    const api_calls = chunks_of_ids.map((ids) =>
      exec_later(get_insights_sender_receiver_counts, ids)
    );

    const res = await Promise.all(api_calls.map(exec));
    if (res.every((l) => l.success)) {
      // @ts-ignore
      const input = res.map((l) => l.value).flat(1);
      mutate(
        update_all_insights,
        input.map((l) => {
          l.isLoaded = true;
          return l;
        })
      );
    }
  }
);

const updateInsightSeverityOptimistic = action<{
  id: string;
  severity: AppsInsightSeverityLevel;
}>(
  "update insight severity level",
  async ({ mutate, state }, { id, severity }) => {
    AppAnalytics.report({
      component: "Apps",
      eventName: "Risk Type Customization app , user updated sensitivity",
    });
    const insight = state.insights.find((ins) => ins.id === id);
    const prev_insight_state = clone(insight)!;
    if (insight && insight.severity !== severity) {
      mutate(
        setInsights,
        state.insights.map((ins) => {
          if (ins.id === id) {
            return {
              ...ins,
              severity,
            };
          }
          return ins;
        })
      );
      const res = await updateInsight({ id, severity });
      if (!res.success) {
        // revert
        mutate(
          setInsights,
          state.insights.map((ins) => {
            if (ins.id === id) {
              return prev_insight_state;
            }
            return ins;
          })
        );
        UseToast().show({
          type: ToastType.ALERT,
          msg: "Error changing risk type severity",
          duration: 3000,
        });
      }
    }
  }
);

export const updateInsightSeverity = action<{
  id: string;
  severity: AppsInsightSeverityLevel;
  optimistic?: boolean;
}>(
  "update insight severity level",
  async ({ mutate, state, dispatch }, { id, severity, optimistic }) => {
    if (optimistic) {
      dispatch(updateInsightSeverityOptimistic, { id, severity });
      return;
    }
    AppAnalytics.report({
      component: "Apps",
      eventName: "Risk Type Customization app , user updated sensitivity",
    });
    const insight = state.insights.find((ins) => ins.id === id);
    if (insight && insight.severity !== severity) {
      const res = await updateInsight({ id, severity });
      if (res.success) {
        mutate(
          setInsights,
          state.insights.map((ins) => {
            if (ins.id === id) {
              return {
                ...ins,
                severity,
              };
            }
            return ins;
          })
        );
      } else {
        UseToast().show({
          type: ToastType.ALERT,
          msg: "Error changing risk type severity",
          duration: 3000,
        });
      }
    }
  }
);

export const insight_by_id = (id: string) =>
  insights.value.find((d) => d.id === id);
// @ts-ignore
window.insight_by_id = insight_by_id;
